/**
 * @workboard/ui-summary wire format for Co-Author:
 * - Plain 1–4: canonical tbl_team_summary.format_choice.
 * - 1_000_000 + 1..3: same as format_choice 1..3.
 * - 1_000_000 + 4: org template; id from genAiBriefCustomId or defaults to template PK 4.
 * - 1_000_000 + n (n > 4): org template id = n (body id wins if present).
 * Yii expects format_choice 4 + genAiBriefCustomId for org templates.
 * The UI may send a layout value (1–3) in format together with genAiBriefCustomId; the wrapper
 * and TeamController then coerce to format 4.
 */
/** Reused by Co-Author team summary; exported for the wrapper that POSTs/PUTs to Yii. */
export function parseOptionalPositiveInt(v: unknown): number | null {
  if (v == null || v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function normalizeTeamSummaryWireToYiiFormat(
  format: unknown,
  genAiBriefCustomId?: unknown,
): { formatChoice: number; genAiBriefCustomId: number | null } {
  const n = Number(format);
  const gidBody = parseOptionalPositiveInt(genAiBriefCustomId);
  if (!Number.isFinite(n)) {
    return { formatChoice: 0, genAiBriefCustomId: gidBody };
  }
  if (n < 1_000_000) {
    // Plain format_choice is only 1–4; same number as template id (>4) is a bad subtract-only decode.
    if (gidBody !== null && n > 4 && n === gidBody) {
      return { formatChoice: 4, genAiBriefCustomId: gidBody };
    }
    return { formatChoice: n, genAiBriefCustomId: gidBody };
  }
  const offset = n - 1_000_000;
  if (offset <= 3) {
    return { formatChoice: offset, genAiBriefCustomId: gidBody };
  }
  if (offset === 4) {
    return { formatChoice: 4, genAiBriefCustomId: gidBody ?? 4 };
  }
  return { formatChoice: 4, genAiBriefCustomId: gidBody ?? offset };
}

/** @deprecated Use normalizeTeamSummaryWireToYiiFormat; subtract-only breaks org template ids. */
export function decodeUiSummaryWireFormat(format: unknown): number {
  return normalizeTeamSummaryWireToYiiFormat(format, undefined).formatChoice;
}

export const mapToConnectSummaryDTO = (summary: any): any => {
  if (!summary) return null;

  const formatChoice = +summary.format_choice;
  const genAiBriefCustomId =
    summary.gen_ai_brief_custom_id != null ? +summary.gen_ai_brief_custom_id : null;
  const wireFormat =
    formatChoice === 4 && genAiBriefCustomId != null
      ? 1_000_000 + genAiBriefCustomId
      : formatChoice;

  const summaryDto = {
    id: +summary.id,
    resourceId: +summary.team_id,
    resourceType: 'team',
    focus: +summary.focus,
    timePeriod: +summary.time_period,
    format: wireFormat,
    language: +summary.language,
    customPrompt: summary.prompt_custom ?? null,
    suggestion: summary.suggestion,
    summary: summary.summary_content ?? summary.suggestion,
    userId: +summary.user_id,
    userFullName: summary.user_name,
    createdAt: summary.created_at,
    updatedAt: summary.updated_at,
    isPublished: +summary.is_published,
    genAiBriefCustomId,
  };
  return summaryDto;
};

export const TEAM_SUMMARY_APIS = {
  GET_SUMMARY: '/wb/team/getPublishedTeamSummary',
  GENERATE_SUMMARY: '/wb/team/generateTeamSummary',
  PUBLISH_SUMMARY: '/wb/team/publishTeamSummary',
  GET_ACCESS_LEVEL: '/wb/team/IsManagerOrCoManagerOrAdmin',
} as const;
